/**
 * La carga guiada de un equipo usado.
 *
 * Tres pasos en el orden en que se piensan con el equipo en la mano:
 *
 *   1. EL EQUIPO — qué es: modelo, memoria, color, batería, IMEI, caja/cable.
 *   2. LOS NÚMEROS — cuánto costó tomarlo y a cuánto se vende (el precio puede
 *      quedar para después). Con o sin precio, el equipo nace sin publicar
 *      (`aNuevoUsado`): a la tienda sale desde Tienda online.
 *   3. REVISIÓN — la ficha completa antes de guardar, como todo en TORA.
 *
 * El mismo formulario sirve para el alta directa, la edición de una ficha y la
 * toma en parte de pago dentro de una venta (`modo="entrega"`): cambia quién
 * recibe el resultado, no lo que se pregunta.
 */
import { memoriaQueSigue } from "@/components/iphones/memorias";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { InputImporte } from "@/components/money/input-importe";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo, GrupoCampo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Desplegable } from "@/components/ui/desplegable";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Pasos } from "@/components/ui/pasos";
import { Segmented } from "@/components/ui/segmented";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SelectorDeColor,
  SelectorDeMemoria,
  SelectorDeModelo,
} from "@/components/iphones/FichaDelEquipo";
import { type Producto } from "@/lib/tipos";

import {
  FORM_USADO_INICIAL,
  OPCIONES_ORIGEN,
  avisosEquipo,
  desdeProductoUsado,
  esDeProveedor,
  nombreUsado,
  pasoDeLoQueFalta,
  resumenUsado,
  validarEquipo,
  validarNumeros,
  type FormUsado,
} from "./logica";

type Paso = "origen" | "equipo" | "numeros" | "revision";

const TITULOS: Record<Paso, string> = {
  origen: "¿De quién es?",
  equipo: "El equipo",
  numeros: "Los números",
  revision: "Revisá",
};

const OPCIONES_CAJA = [
  { valor: "si", etiqueta: "Sí" },
  { valor: "no", etiqueta: "No" },
] as const;

export interface DialogUsadoProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /**
   * "alta" crea o edita la ficha; "entrega" es la toma en parte de pago dentro
   * de una venta; "recepcion" es el equipo del proveedor que finalmente llegó.
   * Los dos últimos tienen el origen decidido por la situación: el equipo está
   * ahí, en la mano. Solo el alta pregunta.
   */
  modo: "alta" | "entrega" | "recepcion";
  /** Con producto, el dialog edita esa ficha en vez de crear. */
  producto?: Producto | null;
  /** Para elegir quién tiene el equipo cuando es «a pedido». */
  proveedores?: ReadonlyArray<{ id: number; nombre: string }>;
  /** Para que el selector de moneda del precio convierta en vez de solo etiquetar. */
  cotizacion?: number;
  /**
   * La moneda con la que arranca un equipo NUEVO (sin `producto`). En la toma
   * en parte de pago es la de la venta: arrancaba siempre en pesos, y en una
   * venta en US$ un «1.115» pensado en dolares cargaba una toma de $ 1.115.
   */
  monedaInicial?: Moneda;
  /** Las fichas que la pantalla tiene cargadas: para frenar un IMEI repetido. */
  productos?: ReadonlyArray<Producto>;
  /**
   * La ficha se creó pero su unidad no entró. El diálogo queda abierto, lo
   * dice, y el botón final repite solo el ingreso (`onReintentar`).
   */
  pendiente?: string | null;
  onReintentar?: () => void;
  guardando?: boolean;
  onGuardar: (form: FormUsado) => void;
  /**
   * Abre la confirmacion de borrado. Solo en edicion de una ficha ya guardada.
   *
   * Un usado se edita ACA y no en la ficha comun, asi que sin este boton el
   * unico producto que no se podia borrar era justamente el que mas duplicados
   * junta: el mismo equipo cargado por la planilla, por el remito y a mano.
   */
  onBorrar?: () => void;
}

/**
 * La ayuda del precio. Decia «sin precio no se ve en la tienda», que se leia
 * como «con precio si»: el equipo se carga siempre sin publicar, con o sin
 * precio, y lo que sale a la web se decide en Tienda online.
 *
 * Despues decia «sin precio no se ofrece», y el mostrador lo ofrecia al costo.
 * Ahora el ticket no deja venderlo sin precio (`usadoSinPrecio`), y la ayuda
 * lo dice.
 */
const AYUDA_PRECIO =
  "Se puede dejar para después. El equipo queda cargado sin publicar: a la tienda sale desde Tienda online, y sin precio no se puede vender en el mostrador.";

export function DialogUsado({
  abierto,
  onAbiertoChange,
  modo,
  producto = null,
  proveedores = [],
  cotizacion,
  monedaInicial,
  productos = [],
  pendiente = null,
  onReintentar,
  guardando = false,
  onGuardar,
  onBorrar,
}: DialogUsadoProps): JSX.Element {
  // El origen solo se pregunta en el alta. En una entrega el cliente te está
  // dando el equipo, y en una recepción acaba de llegar: en los dos casos está
  // en la mano, y preguntarlo sería un paso que siempre se contesta igual.
  const preguntaOrigen = modo === "alta";

  /*
   * LA TOMA EN PARTE DE PAGO NO ES UN WIZARD.
   * -----------------------------------------
   * Pasa en el medio de una venta, con el cliente esperando del otro lado del
   * mostrador y el equipo en la mano. Tres pasos mas una pantalla de revision
   * para contestar "que equipo es y cuanto le doy" es ceremonia que se paga en
   * segundos, cada vez.
   *
   * Asi que en `entrega` se pregunta TODO junto: lo obligatorio a la vista
   * —modelo, memoria, IMEI, bateria y el valor de toma— y lo demas plegado.
   * El alta y la recepcion conservan sus pasos: ahi no hay nadie esperando y
   * la ficha se carga con calma.
   */
  const unSoloPaso = modo === "entrega";
  const PASOS: ReadonlyArray<Paso> = preguntaOrigen
    ? ["origen", "equipo", "numeros", "revision"]
    : unSoloPaso
      ? ["equipo"]
      : ["equipo", "numeros", "revision"];

  const [paso, setPaso] = useState<Paso>(PASOS[0] ?? "equipo");
  const [form, setForm] = useState<FormUsado>(FORM_USADO_INICIAL);
  const [formAlAbrir, setFormAlAbrir] = useState<FormUsado>(FORM_USADO_INICIAL);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [intento, setIntento] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    /**
     * Si la ficha esta marcada como incompleta, el asistente abre DONDE FALTA
     * el dato y con el error ya a la vista (`intento`). El que aprieta editar
     * sobre un equipo con el chip ambar ya sabe que hay algo mal; hacerle
     * recorrer el formulario entero hasta encontrarlo es cobrarle dos veces.
     */
    const faltante = producto ? pasoDeLoQueFalta(producto) : null;
    const arranque = faltante && PASOS.includes(faltante) ? faltante : null;
    setPaso(arranque ?? (preguntaOrigen ? "origen" : "equipo"));
    setIntento(arranque !== null);
    const base = producto
      ? desdeProductoUsado(producto)
      : { ...FORM_USADO_INICIAL, moneda: monedaInicial ?? FORM_USADO_INICIAL.moneda };
    // Recibir es justamente el momento en que el equipo deja de ser del
    // proveedor y pasa a ser del local.
    const inicial = modo === "alta" ? base : { ...base, origen: "local" as const };
    setForm(inicial);
    setFormAlAbrir(inicial);
    setConfirmandoCierre(false);
    // `PASOS` se rearma en cada render pero su contenido depende de `modo`,
    // que ya esta en las dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, producto, modo, preguntaOrigen, monedaInicial]);

  const erroresEquipo = useMemo(
    () =>
      validarEquipo(form, {
        productos,
        propioId: producto?.id ?? null,
        imeiOriginal: producto?.imei ?? null,
      }),
    [form, productos, producto],
  );
  const avisos = useMemo(() => avisosEquipo(form), [form]);
  const erroresNumeros = useMemo(() => validarNumeros(form), [form]);
  // En la pantalla unica no hay "despues": los dos grupos de errores se
  // muestran juntos, porque los dos campos estan a la vista.
  const errores = unSoloPaso
    ? [...erroresEquipo, ...erroresNumeros]
    : paso === "equipo"
      ? erroresEquipo
      : paso === "numeros"
        ? erroresNumeros
        : [];

  const nombreProveedor = proveedores.find((p) => p.id === form.proveedorId)?.nombre ?? null;

  function cambiar(cambios: Partial<FormUsado>): void {
    setForm((f) => ({ ...f, ...cambios }));
  }

  function avanzar(): void {
    if (errores.length > 0) {
      setIntento(true);
      return;
    }
    setIntento(false);
    const i = PASOS.indexOf(paso);
    const siguiente = PASOS[i + 1];
    if (siguiente) setPaso(siguiente);
  }

  function guardar(): void {
    if (errores.length > 0) {
      setIntento(true);
      return;
    }
    onGuardar(form);
  }

  function volver(): void {
    setIntento(false);
    const i = PASOS.indexOf(paso);
    const anterior = PASOS[i - 1];
    if (anterior) setPaso(anterior);
  }

  const tocado = JSON.stringify(form) !== JSON.stringify(formAlAbrir);

  /**
   * Cerrar sin guardar pierde lo cargado, y en esta guía eso son cuatro pasos.
   * Con algo escrito se pregunta; sin nada, cierra como cualquier diálogo.
   * La pregunta va adentro del diálogo y no en otro encima: nunca hay un
   * diálogo abierto arriba de otro (ver `DialogBorrarProducto` en Catálogo).
   *
   * MIENTRAS GUARDA, CERRAR NO HACE NADA.
   * --------------------------------------
   * `cargar()` son dos llamadas (crear la ficha, después ingresar la unidad) y
   * la pantalla que abrió este diálogo llama a `olvidar()` cuando se cierra.
   * Si eso pasa con el ingreso todavía en vuelo y la segunda llamada termina
   * fallando, el `pendiente` que arma el hook queda pisado: la próxima vez que
   * se abra este diálogo no hay ningún aviso, y la ficha sigue sin su unidad
   * sin que nada lo diga. Ni Escape, ni un clic afuera, ni la X, ni Cancelar
   * pueden cerrar mientras `guardando` es true — los cuatro pasan por acá,
   * porque los cuatro terminan en `onOpenChange`/`pedirCierre`.
   */
  function pedirCierre(): void {
    if (guardando) return;
    if (tocado || pendiente) setConfirmandoCierre(true);
    else onAbiertoChange(false);
  }

  const titulo =
    modo === "entrega"
      ? "Equipo en parte de pago"
      : modo === "recepcion"
        ? "Recibir el equipo"
        : producto
          ? "Editar usado"
          : "Cargar un usado";

  const descripcion =
    modo === "entrega"
      ? "El valor de toma descuenta del total a cobrar, y el equipo entra al stock."
      : modo === "recepcion"
        ? "Llegó el equipo que estaba a pedido: cargá el IMEI y pasa a ser stock del local."
        : "Cada unidad usada es un producto propio, con su ficha, su costo y su precio.";

  const verboFinal =
    modo === "entrega"
      ? "Tomar en parte de pago"
      : modo === "recepcion"
        ? "Recibir y pasar a stock"
        : producto
          ? "Guardar cambios"
          : esDeProveedor(form)
            ? "Cargar como a pedido"
            : "Cargar el equipo";

  const indice = PASOS.indexOf(paso);
  // "Ultimo" es el ultimo de la lista, no literalmente la revision: con un
  // solo paso, ese paso ya es el final y el boton tiene que decir el verbo.
  const ultimo = indice === PASOS.length - 1;

  /*
   * Los campos del equipo se declaran UNA vez y se componen en dos layouts: el
   * de la pantalla unica (`entrega`) y el del paso del wizard. Escribirlos dos
   * veces era la otra opcion, y la que garantiza que dentro de seis meses uno
   * de los dos tenga un placeholder viejo.
   */
  /*
   * EL MODELO SE ELIGE, NO SE ESCRIBE.
   * ---------------------------------
   * Era un campo de texto, y "16 pro max", "16 PROMAX" y "16 Pro Max" eran
   * tres productos distintos para la base, tres filas en la lista y tres
   * respuestas al buscar. Nada lo podia corregir despues: el modelo no tiene
   * columna, vive adentro del nombre. Los modelos que Apple hizo son una lista
   * cerrada y ya la teniamos —es la misma tabla que dice en que colores viene
   * cada uno—, asi que la pregunta se contesta eligiendo.
   *
   * Es el mismo control que el alta de sellados: un equipo se describe igual
   * venga nuevo o usado.
   */
  const campoModelo = (
    <SelectorDeModelo
      autoFocus
      modelo={form.modelo}
      onChange={(modelo) =>
        cambiar({ modelo, color: "", memoriaGb: memoriaQueSigue(modelo, form.memoriaGb) })
      }
      ayuda="Como en el mostrador: primero el número, después la variante."
      // Por el local pasan usados de otras marcas: sin «Otro» no se podrían
      // cargar.
      permiteOtro
    />
  );

  const campoMemoria = (
    <SelectorDeMemoria
      modelo={form.modelo}
      memoriaGb={form.memoriaGb}
      onChange={(memoriaGb) => cambiar({ memoriaGb })}
    />
  );

  const campoBateria = (
    <Campo label="Batería %" requerido>
      <Input
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="88"
        value={form.bateriaPct === null ? "" : String(form.bateriaPct)}
        onChange={(e) => {
          const crudo = e.target.value.replace(/[^\d]/g, "");
          cambiar({ bateriaPct: crudo === "" ? null : Math.min(Number(crudo), 100) });
        }}
      />
    </Campo>
  );

  /*
   * El color sale de los que ESE modelo tiene. Escrito a mano entraba como
   * "BLACK", "Black", "NEGRO" y "space black", y el cuadradito de la lista no
   * podia dibujar ninguno.
   *
   * No hay opcion «Todos»: este equipo esta en la mano y tiene un color. La
   * fila de lista de precios —la que viene en todos— es cosa de los sellados.
   */
  const campoColor = (
    <SelectorDeColor
      modelo={form.modelo}
      color={form.color}
      onChange={(color) => cambiar({ color })}
      permiteEscribir
    />
  );

  // La ayuda del IMEI decia de donde sacarlo Y por que importa, en cuatro
  // renglones arriba del campo. Queda el dato operativo; el por que ya lo dice
  // el error cuando falta.
  const campoImei = (
    <Campo
      label="IMEI"
      requerido={!form.imeiPendiente}
      ayuda="Marcá *#06#, o Ajustes → General → Información."
    >
      <Input
        inputMode="numeric"
        placeholder="15 dígitos"
        value={form.imei}
        onChange={(e) => cambiar({ imei: e.target.value })}
        disabled={form.imeiPendiente}
      />
    </Campo>
  );

  // La salida honesta para cuando no se lo puede leer. Sin esto el IMEI se
  // inventa, y un numero inventado ocupa el lugar del real y choca con el
  // proximo invento igual.
  const casillaImei = (
    <Checkbox
      label="No lo tengo a mano ahora"
      ayuda="El equipo entra igual y queda marcado en la lista hasta que cargues el IMEI."
      checked={form.imeiPendiente}
      onChange={(e) => cambiar({ imeiPendiente: e.target.checked })}
    />
  );

  const campoProveedor = (
    <Campo label="¿Quién lo tiene?" ayuda="Es a quién hay que llamar cuando el cliente confirme.">
      <Select
        value={form.proveedorId === null ? "" : String(form.proveedorId)}
        onValueChange={(v) => cambiar({ proveedorId: v ? Number(v) : null })}
      >
        <SelectTrigger aria-label="Proveedor">
          <SelectValue placeholder="Elegí el proveedor" />
        </SelectTrigger>
        <SelectContent>
          {proveedores.map((pr) => (
            <SelectItem key={pr.id} value={String(pr.id)}>
              {pr.nombre}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Campo>
  );

  const campoCajaCable = (
    <GrupoCampo label="¿Trae caja y cable?">
      <Segmented
        etiqueta="¿Trae caja y cable?"
        // Del alto del campo de la garantía, que va al lado.
        tamano="md"
        opciones={OPCIONES_CAJA}
        valor={form.cajaCable ? "si" : "no"}
        onChange={(v) => cambiar({ cajaCable: v === "si" })}
      />
    </GrupoCampo>
  );

  const campoGarantia = (
    <Campo label="Garantía (meses)">
      <Input
        inputMode="numeric"
        value={form.garantiaMeses === null ? "" : String(form.garantiaMeses)}
        onChange={(e) => {
          const crudo = e.target.value.replace(/[^\d]/g, "");
          cambiar({ garantiaMeses: crudo === "" ? null : Number(crudo) });
        }}
      />
    </Campo>
  );

  const campoObservaciones = (
    <Campo label="Observaciones">
      <Input
        placeholder="Marca en el marco, vidrio nuevo…"
        value={form.observaciones}
        onChange={(e) => cambiar({ observaciones: e.target.value })}
      />
    </Campo>
  );

  return (
    <Dialog open={abierto} onOpenChange={(a) => (a ? onAbiertoChange(true) : pedirCierre())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {/* La descripcion del dialogo (aria-describedby) es el texto del modo
              mas "Paso N de M · titulo", que `Pasos` escribe arriba de la barra. */}
          <DialogDescription asChild>
            <div className="space-y-2">
              <p>{descripcion}</p>
              {/* Una barra de "paso 1 de 1" no informa nada: solo ocupa. */}
              {PASOS.length > 1 ? (
                <Pasos pasos={PASOS.map((p) => TITULOS[p])} actual={indice} />
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {paso === "origen" ? (
            <div role="group" aria-label={TITULOS.origen} className="flex flex-col gap-2">
              {/* Tarjetas-opcion: la elegida queda marcada con aria-pressed, y
                  tocarla ya avanza. Es una pregunta de una sola respuesta: un
                  "Siguiente" despues de elegir era un toque de mas. Al volver
                  con "Atras" la respuesta sigue marcada. */}
              {OPCIONES_ORIGEN.map((o) => (
                <Button
                  key={o.origen}
                  type="button"
                  variant="outline"
                  aria-pressed={form.origen === o.origen}
                  onClick={() => {
                    cambiar({ origen: o.origen });
                    setIntento(false);
                    setPaso("equipo");
                  }}
                  className="h-auto w-full justify-start whitespace-normal px-4 py-3 text-left aria-pressed:border-primary aria-pressed:bg-accent"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{o.titulo}</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      {o.detalle}
                    </span>
                  </span>
                </Button>
              ))}
            </div>
          ) : null}

          {paso === "equipo" && unSoloPaso ? (
            <div className="space-y-4">
              {campoModelo}
              {campoMemoria}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {campoImei}
                {campoBateria}
              </div>
              {casillaImei}

              {/* El valor de toma sube al primer plano: es la mitad de la
                  pregunta que se esta contestando, no un paso aparte. */}
              <InputImporte
                label="¿Cuánto se le reconoce por el equipo?"
                valor={form.costo}
                onValorChange={(v) => cambiar({ costo: v })}
                moneda={form.moneda}
                onMonedaChange={(m) => cambiar({ moneda: m })}
                cotizacion={cotizacion}
                requerido
              />

              {/*
                Lo que casi siempre queda como viene. Plegado no desaparece:
                sigue en el formulario y se guarda igual. `forzarAbierto` lo
                abre si la validacion falla, para que un error del precio no
                apunte a un campo que no se ve.
              */}
              <Desplegable resumen="Más detalles" forzarAbierto={intento && errores.length > 0}>
                {campoColor}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{campoGarantia}</div>
                {campoCajaCable}
                {campoObservaciones}
                <InputImporte
                  label="Precio de venta"
                  valor={form.precio}
                  onValorChange={(v) => cambiar({ precio: v })}
                  moneda={form.moneda}
                  ayuda={AYUDA_PRECIO}
                />
              </Desplegable>

              {avisos.length > 0 ? (
                <Aviso tono="warning" compacto>
                  {avisos.join(" ")}
                </Aviso>
              ) : null}
            </div>
          ) : null}

          {paso === "equipo" && !unSoloPaso ? (
            <div className="space-y-4">
              {campoModelo}
              {campoMemoria}
              {/* El color va al ancho entero: son cuatro o cinco chips con
                  nombres largos («Natural Titanium»), y en media grilla se
                  parten en tres renglones. */}
              {campoColor}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{campoBateria}</div>

              {esDeProveedor(form) ? (
                campoProveedor
              ) : (
                <div className="space-y-1.5">
                  {campoImei}
                  {casillaImei}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {campoCajaCable}
                {campoGarantia}
              </div>

              {campoObservaciones}

              {avisos.length > 0 ? (
                <Aviso tono="warning" compacto>
                  {avisos.join(" ")}
                </Aviso>
              ) : null}
            </div>
          ) : null}

          {paso === "numeros" ? (
            <div className="space-y-4">
              <Card variant="muted" padding="compacto" className="text-sm font-medium">
                {nombreUsado(form)}
              </Card>
              <InputImporte
                label={
                  modo === "entrega"
                    ? "¿Cuánto se le reconoce por el equipo?"
                    : esDeProveedor(form)
                      ? "Costo: a cuánto te lo deja el proveedor"
                      : "Costo de toma: cuánto se pagó por el equipo"
                }
                valor={form.costo}
                onValorChange={(v) => cambiar({ costo: v })}
                moneda={form.moneda}
                onMonedaChange={(m) => cambiar({ moneda: m })}
                cotizacion={cotizacion}
                requerido
                autoFocus
              />
              <InputImporte
                label="Precio de venta (se puede dejar para después)"
                valor={form.precio}
                onValorChange={(v) => cambiar({ precio: v })}
                moneda={form.moneda}
                ayuda={AYUDA_PRECIO}
              />
            </div>
          ) : null}

          {paso === "revision" ? (
            <>
              {modo === "alta" ? (
                <Aviso
                  tono={esDeProveedor(form) ? "info" : "success"}
                  data-testid="destino-usado"
                  titulo={
                    esDeProveedor(form) ? "A pedido: no entra al stock" : "Entra al stock: 1 unidad"
                  }
                >
                  {esDeProveedor(form)
                    ? "Lo tiene el proveedor. Queda en la lista para venderlo a pedido."
                    : "Está en el local y se puede vender desde ahora."}
                </Aviso>
              ) : null}
              <Card variant="muted" padding="compacto" className="text-sm">
                <ul className="space-y-1">
                  {resumenUsado(form, nombreProveedor).map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                  {modo === "entrega" ? (
                    <li className="pt-1 text-xs text-muted-foreground">
                      El valor de toma se descuenta del total de la venta y el equipo entra al
                      stock.
                    </li>
                  ) : null}
                </ul>
              </Card>
            </>
          ) : null}

          {pendiente ? (
            <Aviso tono="destructive" compacto titulo="Falta la unidad">
              {pendiente}
            </Aviso>
          ) : null}

          {confirmandoCierre && pendiente ? (
            /*
              La ficha YA EXISTE (quedó en stock 0): "¿Descartás lo que
              cargaste?" sería mentira, porque nada se pierde. Lo que falta es
              la unidad, y las dos salidas son terminar el ingreso o dejarla
              así hasta la próxima vez.
            */
            <Aviso tono="warning" titulo="La ficha ya quedó creada, sin la unidad">
              <p>Si cerrás ahora, el equipo no aparece en Usados hasta que ingreses la unidad.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setConfirmandoCierre(false);
                    onReintentar?.();
                  }}
                >
                  Reintentar el ingreso
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost-destructive"
                  onClick={() => {
                    setConfirmandoCierre(false);
                    onAbiertoChange(false);
                  }}
                >
                  Cerrar igual
                </Button>
              </div>
            </Aviso>
          ) : confirmandoCierre ? (
            <Aviso tono="warning" titulo="¿Descartás lo que cargaste?">
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmandoCierre(false)}
                >
                  Seguir cargando
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost-destructive"
                  onClick={() => {
                    setConfirmandoCierre(false);
                    onAbiertoChange(false);
                  }}
                >
                  Descartar
                </Button>
              </div>
            </Aviso>
          ) : null}

          {intento && errores.length > 0 ? (
            <Aviso tono="destructive" compacto>
              <ul className="space-y-1">
                {errores.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Aviso>
          ) : null}
        </DialogBody>

        <DialogFooter variant="wizard">
          {/*
              Cancelar y Borrar comparten el lado izquierdo: los dos sacan de
              acá sin guardar. Borrar solo aparece editando una ficha que ya
              existe —una entrega o una recepción están creando algo— y
              destenido, que es lo que corresponde a lo que no tiene vuelta.
          */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="ghost" onClick={pedirCierre}>
              Cancelar
            </Button>
            {producto && modo === "alta" && onBorrar ? (
              <Button type="button" variant="ghost-destructive" onClick={onBorrar}>
                <Trash2 aria-hidden="true" />
                Borrar equipo
              </Button>
            ) : null}
          </div>

          {/* En mobile se apilan con la primaria arriba, igual que el footer. */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            {/*
                Con la ficha pendiente, "Atrás" desaparece: reintentar repite
                SOLO el ingreso contra la ficha ya creada, y una edición hecha
                después de volver quedaría silenciosamente ignorada (ver
                `onReintentar`, más abajo).
            */}
            {indice > 0 && !pendiente ? (
              <Button type="button" variant="outline" onClick={volver} disabled={guardando}>
                <ChevronLeft aria-hidden="true" />
                Atrás
              </Button>
            ) : null}
            {ultimo ? (
              /*
                El boton final VALIDA antes de guardar. En el wizard no hacia
                falta —cada `avanzar()` validaba su paso y a la revision no se
                llegaba con errores— pero en la pantalla unica el boton final
                es tambien el primer control que ve los datos. Sin esto, una
                toma sin IMEI ni valor se guardaba igual.
              */
              pendiente && onReintentar ? (
                <Button type="button" onClick={onReintentar} cargando={guardando}>
                  Reintentar el ingreso
                </Button>
              ) : (
                <Button type="button" onClick={guardar} cargando={guardando}>
                  {verboFinal}
                </Button>
              )
            ) : paso === "origen" ? null : (
              <Button type="button" onClick={avanzar}>
                Siguiente
                <ChevronRight aria-hidden="true" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
